import React from 'react';
import cn from 'classnames';
import { CustodianTypes, User } from 'services/requests';
import { useRole } from 'hooks/common/useRole';
import { useResendInviteIraUser, useInviteIraUser } from 'hooks/api/iraUser';
import { useModalState } from 'hooks/modals';
import { checkUser } from 'utils';
import { USER_STATUS } from 'constants/status';
import { USER_TYPE } from 'constants/userTypes';
import { InviteIcon_24 } from 'assets/icons';
import ConfirmInviteModal from 'components/ConfirmInviteModal/ConfirmInviteModal';
import { showInfoToast } from 'components/Toast';
import Button from 'components/Button';
import StatusUserLockModal from 'components/StatusUserLockModal';
import StatusUserUnlockModal from 'components/StatusUserUnlockModal';
import ContactSupportButton from 'components/ContactSupportButton';

import messages from './StatusUserButton.messages';
import styles from './StatusUserButton.module.css';

type Props<T> = {
  user?: T;
  userType: string;
};

const StatusUserButton = <T extends User>({ user, userType }: Props<T>): JSX.Element => {
  const { isAdmin } = useRole();
  const {
    isModalVisible: isLockModalVisible,
    openModal: openLockModal,
    closeModal: closeLockModal,
  } = useModalState();
  const {
    payload: isCancelAutoLock,
    isModalVisible: isUnlockModalVisible,
    openModal: openUnlockModal,
    closeModal: closeUnlockModal,
  } = useModalState(false);
  const {
    isModalVisible: isConfirmInviteModalVisible,
    openModal: openConfirmInviteModal,
    closeModal: closeConfirmInviteModal,
    payload: confirmInviteModalPayload,
  } = useModalState<{ isResend: boolean }>();

  const { mutateAsync: inviteIraUser, isLoading: inviteIraUserLoading } =
    useInviteIraUser();
  const { mutateAsync: resendInviteIraUser, isLoading: resendInviteIraUserLoading } =
    useResendInviteIraUser();

  const isInviteLoading = inviteIraUserLoading || resendInviteIraUserLoading;

  const isInvitedStatus = user?.profileStatus === USER_STATUS.invited;
  const isDraftStatus = user?.profileStatus === USER_STATUS.draft;
  const isAccountCreationFailedStatus =
    user?.profileStatus === USER_STATUS.accountCreationFailed;

  const hasUserManager = !!user?.userManagers?.length;

  const getInviteButtonText = () => {
    if (hasUserManager) {
      return messages.notifyUserManager;
    }
    return messages.moveToInvited;
  };

  const onInviteClick = async (): Promise<void> => {
    if (checkUser(user))
      switch (userType) {
        case USER_TYPE.iraUser:
          const inviteIraResult = await inviteIraUser(user.id.toString());
          if (inviteIraResult.success) {
            if (hasUserManager) {
              showInfoToast(messages.successEmailToUserManager);
            } else {
              showInfoToast(messages.successMoveToInvited);
            }
          }
          return;
        case USER_TYPE.independentIra:
          openConfirmInviteModal({ isResend: false });
          return;
      }
  };

  const onResendInviteClick = async (): Promise<void> => {
    if (checkUser(user))
      switch (userType) {
        case USER_TYPE.iraUser:
          const resendInviteIraResult = await resendInviteIraUser(user.id.toString());
          if (resendInviteIraResult.success) {
            if (hasUserManager) {
              showInfoToast(messages.successEmailToUserManager);
            } else {
              showInfoToast(messages.successMoveToInvited);
            }
          }
          return;

        case USER_TYPE.independentIra:
          openConfirmInviteModal({ isResend: true });
          return;
      }
  };

  const onLockClick = (): void => {
    openLockModal();
  };

  const onUnlockClick = (isCancelAutoLock: boolean): void => {
    openUnlockModal(isCancelAutoLock);
  };

  const RenderLockUnlockOption = (): JSX.Element | null => {
    if (isDraftStatus || !isAdmin) return null;

    return (
      <React.Fragment>
        {user?.isLocked ? (
          <Button
            variant={'outline'}
            className={styles.statusUserButton__lock}
            onClick={() => onUnlockClick(false)}
          >
            {messages.unlock}
          </Button>
        ) : (
          <Button
            variant={'outline'}
            className={styles.statusUserButton__lock}
            onClick={onLockClick}
          >
            {messages.lock}
          </Button>
        )}

        {user?.isAutoLocked && !user.isLocked ? (
          <Button
            className={styles.statusUserButton__cancelAutolock}
            onClick={() => onUnlockClick(true)}
          >
            {messages.cancelAutolock}
          </Button>
        ) : null}
      </React.Fragment>
    );
  };

  const RenderInviteOption = (): JSX.Element | null => {
    if (user?.custodianType === CustodianTypes.solera) return null;
    if (!(isDraftStatus || isInvitedStatus)) return null;
    if (!hasUserManager && isInvitedStatus) return null;
    if (user?.isLocked || user?.isAutoLocked) return null;
    if (isAccountCreationFailedStatus) return null;

    return (
      <Button
        onClick={isInvitedStatus ? onResendInviteClick : onInviteClick}
        className={cn(
          styles.statusUserButton__invite,
          isInvitedStatus && styles.invited,
          !hasUserManager && styles.statusUserButton__moveToInvited,
        )}
        isLoading={isInviteLoading}
      >
        {!hasUserManager && <InviteIcon_24 />}
        {getInviteButtonText()}
      </Button>
    );
  };

  return (
    <React.Fragment>
      <RenderLockUnlockOption />
      <RenderInviteOption />
      {isAccountCreationFailedStatus && <ContactSupportButton />}

      <StatusUserLockModal
        onRequestClose={closeLockModal}
        isOpen={isLockModalVisible}
        user={user}
        userType={userType}
      />
      <StatusUserUnlockModal
        onRequestClose={closeUnlockModal}
        isOpen={isUnlockModalVisible}
        user={user}
        userType={userType}
        isCancelAutoLock={!!isCancelAutoLock}
      />

      <ConfirmInviteModal
        onRequestClose={closeConfirmInviteModal}
        isOpen={isConfirmInviteModalVisible}
        user={user}
        isResend={confirmInviteModalPayload?.isResend ?? false}
      />
    </React.Fragment>
  );
};

export default StatusUserButton;
